import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@cyphex/ui';
import type { Conversation, ConversationId, UpdateConversationRequest } from '@cyphex/types';
import { chatApi } from '../api';
import { CHAT_KEYS } from '../constants';
import { applyConversationPatch } from '../lib/conversationCache';

interface UseConversationResult {
  conversation: Conversation | null;
  isLoading: boolean;
  isError: boolean;
  /**
   * The failure itself, so a caller can tell "this thread is not mine" (the API
   * answers 404 for a conversation you hold no active membership row in — see
   * `getConversationAccess`) from "the server fell over". `isError` cannot: it
   * is one boolean over both, and the in-call chat rail has to branch on the
   * difference. Narrow it with `isApiRequestError`; never render it.
   */
  error: unknown;
  update: (patch: UpdateConversationRequest) => void;
  resolveRequest: (action: 'accept' | 'block') => void;
}

const NO_CONVERSATION = new Error('No conversation selected.');

/** A single conversation plus the mutations the header and list rows fire. */
export function useConversation(conversationId: ConversationId | null): UseConversationResult {
  const client = useQueryClient();

  const query = useQuery({
    queryKey: conversationId ? CHAT_KEYS.conversation(conversationId) : ['chat', 'conversation', 'none'],
    queryFn: () => {
      if (!conversationId) throw NO_CONVERSATION;
      return chatApi.getConversation(conversationId);
    },
    enabled: Boolean(conversationId),
    staleTime: 30_000,
  });

  const update = useMutation({
    mutationFn: (patch: UpdateConversationRequest) => {
      if (!conversationId) throw NO_CONVERSATION;
      return chatApi.updateConversation(conversationId, patch);
    },
    onSuccess: (conversation) => {
      if (conversationId) applyConversationPatch(client, conversationId, conversation);
    },
    onError: () => toast.error('Could not update this chat. Try again.'),
  });

  const request = useMutation({
    mutationFn: (action: 'accept' | 'block') => {
      if (!conversationId) throw NO_CONVERSATION;
      return chatApi.resolveRequest(conversationId, action);
    },
    onSuccess: (conversation, action) => {
      if (conversationId) applyConversationPatch(client, conversationId, conversation);
      void client.invalidateQueries({ queryKey: ['chat', 'conversations'] });
      toast.success(action === 'accept' ? 'Chat request accepted.' : 'Sender blocked.');
    },
    onError: () => toast.error('Could not update the request. Try again.'),
  });

  return {
    conversation: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    update: update.mutate,
    resolveRequest: request.mutate,
  };
}
